// Global Instructions Rule Applied!
//
// The house error toast (owner decisions 2026-09-02):
//
//   * An error toast PERSISTS until the user dismisses it (✕) — or until the
//     caller reports that the condition has cleared (`dismissErrorToast(key)`,
//     e.g. validation passed on the next Save, the same save later succeeded,
//     a failed proposal was retried successfully). A 2.5 s error that vanished
//     before it was read was as good as no error at all.
//   * It carries a copy control so the user can hand IT/admin the exact text
//     (plus where and when) without a screenshot.
//   * Body clicks do NOT close it (the copy icon must be clickable and the text
//     selectable); the ✕ does.
//   * Same `key` ⇒ the toast is de-duplicated (react-toastify `toastId`), so a
//     repeated failure updates one toast instead of stacking a column of them.
//
// Every `toast.error(...)` in the app goes through here; `toast.success` /
// `toast.info` keep the short container defaults — they confirm, they do not
// need reading.

import React from 'react'
import { toast, type ToastOptions } from 'react-toastify'
import { CopyButton } from '../components/CopyButton.tsx'

export type ErrorToastOptions = ToastOptions & {
  /** Stable identity for de-dup and for `dismissErrorToast`. */
  key?: string
  /** What the copy icon puts on the clipboard; defaults to the message text. */
  copyText?: string
}

const isPlainText = (content: React.ReactNode): content is string | number =>
  typeof content === 'string' || typeof content === 'number'

/** "<message>\n<route> · <local time>" — the message plus the two facts
 *  support always asks for next. */
export const describeErrorForSupport = (message: string): string => {
  const where = typeof window !== 'undefined' ? window.location.pathname : ''
  const when = new Date().toLocaleString()
  return `${message}\n${where} · ${when}`.trim()
}

/** Message text + inline copy icon. Exported so a caller that composes its
 *  own JSX message can still get the copy affordance. */
export function ErrorToastContent({ message, copyText }: { message: React.ReactNode; copyText: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message}</div>
      <CopyButton value={copyText} label='Copy error' iconOnly />
    </div>
  )
}

/** Drop-in for `toast.error(message, options)`. */
export const showErrorToast = (message: React.ReactNode, options: ErrorToastOptions = {}) => {
  const { key, copyText, ...rest } = options
  const text = copyText ?? (isPlainText(message) ? String(message) : '')
  const content = text ? <ErrorToastContent message={message} copyText={describeErrorForSupport(text)} /> : message
  return toast.error(content, {
    ...rest,
    ...(key ? { toastId: key } : {}),
    // Owner rule: errors stay until read.
    autoClose: false,
    // The copy icon and text selection must not dismiss the toast.
    closeOnClick: false
  })
}

/** The error condition has cleared — take the toast down without waiting for
 *  the user. Safe to call when no such toast is showing. */
export const dismissErrorToast = (key: string) => {
  if (toast.isActive(key)) toast.dismiss(key)
}
